import { useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FormData } from "@/components/AdvisorWizard";
import { Json } from "@/integrations/supabase/types";

const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export const useWizardTracking = () => {
  const sessionIdRef = useRef<string | null>(null);
  const isInitializedRef = useRef(false);

  // Initialize session when wizard starts
  const initSession = useCallback(async () => {
    if (isInitializedRef.current) return sessionIdRef.current;
    
    const sessionId = generateSessionId();
    sessionIdRef.current = sessionId;
    isInitializedRef.current = true;

    try {
      await supabase.from("wizard_sessions").insert({
        session_id: sessionId,
        current_step: "intro",
        user_agent: navigator.userAgent,
        referrer: document.referrer || null,
      });
    } catch (error) {
      console.error("Error initializing wizard session:", error);
    }

    return sessionId;
  }, []);

  // Track step changes and form data
  const trackStep = useCallback(async (
    step: string,
    formData: Partial<FormData>
  ) => {
    if (!sessionIdRef.current) {
      await initSession();
    }

    try {
      const updateData: Record<string, unknown> = {
        current_step: step,
      };

      // Map form data to database columns
      if (formData.age) updateData.age_range = formData.age;
      if (formData.familyStatus) updateData.family_status = formData.familyStatus;
      if (formData.financialSituation) updateData.financial_situation = formData.financialSituation;
      if (formData.mainReason) updateData.main_reason = formData.mainReason;
      if (formData.propertyType) updateData.property_type = formData.propertyType;
      if (formData.estimatedValue) updateData.property_value = formData.estimatedValue;
      if (formData.location) updateData.location = formData.location;
      if (formData.paymentForm) updateData.payment_form = formData.paymentForm;
      if (formData.priority) updateData.priority = formData.priority;
      if (formData.monthlyPayments) updateData.monthly_payment = formData.monthlyPayments;
      if (formData.futurePlans) updateData.future_plans = formData.futurePlans;

      await supabase
        .from("wizard_sessions")
        .update(updateData)
        .eq("session_id", sessionIdRef.current);
    } catch (error) {
      console.error("Error tracking wizard step:", error);
    }
  }, [initSession]);

  // Mark session as completed with recommendations
  const trackCompletion = useCallback(async (
    formData: FormData,
    recommendations: Json
  ) => {
    if (!sessionIdRef.current) return;

    try {
      await supabase
        .from("wizard_sessions")
        .update({
          current_step: "results",
          completed_at: new Date().toISOString(),
          recommendations: recommendations,
          // Final form data
          age_range: formData.age,
          family_status: formData.familyStatus,
          financial_situation: formData.financialSituation,
          main_reason: formData.mainReason,
          property_type: formData.propertyType,
          property_value: formData.estimatedValue,
          location: formData.location,
          payment_form: formData.paymentForm,
          priority: formData.priority,
          monthly_payment: formData.monthlyPayments,
          future_plans: formData.futurePlans,
        })
        .eq("session_id", sessionIdRef.current);
    } catch (error) {
      console.error("Error tracking wizard completion:", error);
    }
  }, []);

  // Reset tracking for new session
  const resetTracking = useCallback(() => {
    sessionIdRef.current = null;
    isInitializedRef.current = false;
  }, []);

  return {
    initSession,
    trackStep,
    trackCompletion,
    resetTracking,
  };
};
